import { mdiChevronDown } from '@mdi/js';
import { Dropdown } from 'antd';
import Icon from 'components/Icon/Icon';
import NavLink from 'components/NavLink/NavLink';
import { useCustomTranslation } from 'helpers/translationHelper';
import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

interface HeaderItem {
  to: string;
  label: React.ReactNode;
  exact?: boolean;
  hidden?: boolean;
  activeRoutes?: string[];
  inactiveRoutes?: string[];
}

interface HeaderItemsProps {
  items: HeaderItem[];
}

const HeaderItems = ({ items }: HeaderItemsProps) => {
  const { tCommon } = useCustomTranslation();
  const moreLabel = tCommon('more');
  const location = useLocation();

  // Hidden flag is resolved in parent and applied here as a pure projection.
  const visibleItems = useMemo(() => items.filter((item) => !item.hidden), [items]);
  // Number of items rendered directly in the header bar.
  const [visibleCount, setVisibleCount] = useState(visibleItems.length);

  const containerRef = useRef<HTMLUListElement | null>(null);
  const measureItemRefs = useRef<Array<HTMLLIElement | null>>([]);
  const moreMeasureRef = useRef<HTMLDivElement | null>(null);

  const recalculateVisibleItems = useCallback(() => {
    if (!containerRef.current) {
      return;
    }

    // Measure available space from the parent so the result is independent of how
    // many items are currently rendered inside the container itself.
    const availableWidth = Math.max(
      0,
      Math.floor(
        (containerRef.current.parentElement ?? containerRef.current).getBoundingClientRect().width
      ) - 8
    );

    if (!availableWidth) {
      return;
    }

    // Widths are taken from a hidden measurement list that uses the same CSS classes
    // as the visible list to keep typography/padding measurements consistent.
    const itemWidths = visibleItems.map(
      (_, index) => measureItemRefs.current[index]?.offsetWidth || 0
    );
    const moreItemWidth = moreMeasureRef.current?.offsetWidth || 0;

    // On first paint refs can be empty; wait for next layout pass.
    if (itemWidths.some((width) => width === 0) || moreItemWidth === 0) {
      return;
    }

    let usedWidth = 0;
    let nextVisibleCount = visibleItems.length;

    for (let index = 0; index < itemWidths.length; index += 1) {
      const remainingItems = itemWidths.length - index - 1;
      // Reserve width for the "More" trigger if any item would overflow.
      const reservedWidth = remainingItems > 0 ? moreItemWidth : 0;

      if (usedWidth + itemWidths[index] + reservedWidth > availableWidth) {
        nextVisibleCount = index;
        break;
      }

      usedWidth += itemWidths[index];
    }

    setVisibleCount((currentVisibleCount) =>
      currentVisibleCount === nextVisibleCount ? currentVisibleCount : nextVisibleCount
    );
  }, [visibleItems]);

  useLayoutEffect(() => {
    // Measure immediately after layout, then once again in RAF to stabilize the first paint.
    recalculateVisibleItems();

    const rafId = window.requestAnimationFrame(recalculateVisibleItems);
    const resizeObserver = new ResizeObserver(recalculateVisibleItems);

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    // Parent width can change independently because of sibling content (user menu/lang switch).
    if (containerRef.current?.parentElement) {
      resizeObserver.observe(containerRef.current.parentElement);
    }

    return () => {
      window.cancelAnimationFrame(rafId);
      resizeObserver.disconnect();
    };
  }, [moreLabel, recalculateVisibleItems]);

  const mainItems = visibleItems.slice(0, visibleCount);
  const overflowItems = visibleItems.slice(visibleCount);
  // True when no items fit inline and everything is collapsed into the trigger.
  const isCollapsed = visibleCount === 0;

  // Find the currently active item to display its label on the trigger in collapsed mode.
  // Computed unconditionally so it is always ready when isCollapsed becomes true.
  const activeItem = useMemo(() => {
    const sorted = [...visibleItems].sort((a, b) => b.to.length - a.to.length);

    return (
      sorted.find((item) => {
        if (item.activeRoutes) {
          return item.activeRoutes.some((pattern) => new RegExp(pattern).test(location.pathname));
        }
        if (item.exact) return location.pathname === item.to;
        if (item.to === '/') return false; // '/' without exact would match everything
        return location.pathname.startsWith(item.to);
      }) ??
      // fallback to the home item
      visibleItems.find((item) => item.to === '/') ??
      visibleItems[0]
    );
  }, [visibleItems, location.pathname]);

  const overflowMenuItems = overflowItems.map((item) => ({
    key: item.to,
    label: (
      <NavLink
        label={item.label}
        to={item.to}
        activeRoutes={item.activeRoutes}
        exact={item.exact}
        inactiveRoutes={item.inactiveRoutes}
      />
    )
  }));

  return (
    <>
      <ul ref={containerRef} className="HeaderItems">
        {mainItems.map((item) => (
          <li key={item.to} className="nav-item">
            <NavLink
              label={item.label}
              to={item.to}
              activeRoutes={item.activeRoutes}
              exact={item.exact}
              inactiveRoutes={item.inactiveRoutes}
            />
          </li>
        ))}

        {overflowItems.length > 0 && (
          <li className="nav-item HeaderItems_more">
            <Dropdown
              trigger={['click']}
              classNames={{
                root: 'HeaderItems_moreMenu'
              }}
              menu={{ items: overflowMenuItems }}
            >
              <div className={`NavLink HeaderItems_moreTrigger${isCollapsed ? ' active' : ''}`}>
                <span>{isCollapsed ? activeItem?.label : moreLabel}</span>
                <Icon path={mdiChevronDown} />
              </div>
            </Dropdown>
          </li>
        )}
      </ul>

      <ul className="HeaderItems HeaderItems_measure" aria-hidden="true">
        {/* Hidden mirror list used only for width measurement. */}
        {visibleItems.map((item, index) => (
          <li
            key={`measure-${item.to}`}
            className="nav-item"
            ref={(element) => {
              measureItemRefs.current[index] = element;
            }}
          >
            <NavLink
              label={item.label}
              to={item.to}
              activeRoutes={item.activeRoutes}
              exact={item.exact}
              inactiveRoutes={item.inactiveRoutes}
            />
          </li>
        ))}

        <li className="nav-item">
          <div ref={moreMeasureRef} className="NavLink HeaderItems_moreTrigger">
            <span>{moreLabel}</span>
            <Icon path={mdiChevronDown} />
          </div>
        </li>
      </ul>
    </>
  );
};

export default HeaderItems;
